import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Link,
  Stack,
  Typography,
} from '@mui/material';
import DeleteForeverOutlinedIcon from '@mui/icons-material/DeleteForeverOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import GavelOutlinedIcon from '@mui/icons-material/GavelOutlined';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined';
import PolicyOutlinedIcon from '@mui/icons-material/PolicyOutlined';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { deleteMyAccount, downloadMyData } from '../api/account';
import AccountDeletionDialog from '../components/tavern/AccountDeletionDialog';
import CookiePreferencesPanel from '../components/privacy/CookiePreferencesPanel';
import EmailPreferencesPanel from '../components/privacy/EmailPreferencesPanel';
import GoogleCalendarPanel from '../components/privacy/GoogleCalendarPanel';
import PushPreferencesPanel from '../components/privacy/PushPreferencesPanel';
import { loggedOut } from '../features/auth/authSlice';
import { clearAccountLocalData } from '../utils/accountLocalData';

const SettingsSection = ({ children, eyebrow, title, subtitle }) => (
  <Stack spacing={2}>
    <Box>
      <Typography variant="subtitle2" color="secondary.dark" sx={{ mb: 0.5 }}>
        {eyebrow}
      </Typography>
      <Typography variant="h3">{title}</Typography>
      {subtitle ? (
        <Typography color="text.secondary" sx={{ mt: 0.75, maxWidth: 720 }}>
          {subtitle}
        </Typography>
      ) : null}
    </Box>
    {children}
  </Stack>
);

const PrivacyDataSettingsPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [exportingData, setExportingData] = useState(false);
  const [exportError, setExportError] = useState(false);
  const [exportSuccess, setExportSuccess] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingAccount, setDeletingAccount] = useState(false);
  const [deleteError, setDeleteError] = useState(false);

  const handleDownloadMyData = async () => {
    setExportingData(true);
    setExportError(false);
    setExportSuccess(false);

    try {
      await downloadMyData();
      setExportSuccess(true);
    } catch {
      setExportError(true);
    } finally {
      setExportingData(false);
    }
  };

  const handleDeleteAccount = async () => {
    setDeletingAccount(true);
    setDeleteError(false);

    try {
      await deleteMyAccount();
      clearAccountLocalData();
      dispatch(loggedOut());
      navigate('/login', {
        replace: true,
        state: { accountDeleted: true },
      });
    } catch {
      setDeleteError(true);
      setDeletingAccount(false);
    }
  };

  const legalCards = [
    {
      icon: PolicyOutlinedIcon,
      title: t('settingsPrivacy.legal.privacy.title'),
      copy: t('settingsPrivacy.legal.privacy.copy'),
      to: '/privacy',
      action: t('settingsPrivacy.legal.privacy.action'),
    },
    {
      icon: GavelOutlinedIcon,
      title: t('settingsPrivacy.legal.terms.title'),
      copy: t('settingsPrivacy.legal.terms.copy'),
      to: '/terms',
      action: t('settingsPrivacy.legal.terms.action'),
    },
    {
      icon: MailOutlineIcon,
      title: t('settingsPrivacy.legal.requests.title'),
      copy: t('settingsPrivacy.legal.requests.copy'),
      to: '/privacy/contact',
      action: t('settingsPrivacy.legal.requests.action'),
    },
  ];

  return (
    <Stack spacing={4}>
      <Box>
        <Typography variant="subtitle2" color="secondary.dark" sx={{ mb: 1 }}>
          {t('settings.eyebrow')}
        </Typography>
        <Typography component="h1" variant="h1">
          {t('settings.title')}
        </Typography>
        <Typography color="text.secondary" sx={{ mt: 1, maxWidth: 720 }}>
          {t('settings.intro')}
        </Typography>
      </Box>

      <SettingsSection
        eyebrow={t('settings.sections.notifications.eyebrow')}
        title={t('settings.sections.notifications.title')}
        subtitle={t('settings.sections.notifications.subtitle')}
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
            gap: 2,
          }}
        >
          <Card variant="outlined">
            <CardContent>
              <Stack spacing={2}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <NotificationsActiveOutlinedIcon color="secondary" />
                  <Typography variant="h4">{t('settings.sections.notifications.email')}</Typography>
                </Stack>
                <EmailPreferencesPanel />
              </Stack>
            </CardContent>
          </Card>

          <Card variant="outlined">
            <CardContent>
              <Stack spacing={2}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <NotificationsActiveOutlinedIcon color="secondary" />
                  <Typography variant="h4">{t('settings.sections.notifications.push')}</Typography>
                </Stack>
                <PushPreferencesPanel />
              </Stack>
            </CardContent>
          </Card>
        </Box>
      </SettingsSection>

      <SettingsSection
        eyebrow={t('settings.sections.integrations.eyebrow')}
        title={t('settings.sections.integrations.title')}
        subtitle={t('settings.sections.integrations.subtitle')}
      >
        <Card variant="outlined">
          <CardContent>
            <GoogleCalendarPanel />
          </CardContent>
        </Card>
      </SettingsSection>

      <SettingsSection
        eyebrow={t('settings.sections.privacy.eyebrow')}
        title={t('settings.sections.privacy.title')}
        subtitle={t('settings.sections.privacy.subtitle')}
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
            gap: 2,
          }}
        >
          <Card variant="outlined">
            <CardContent>
              <Stack spacing={2} alignItems="flex-start">
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: '50%',
                    display: 'grid',
                    placeItems: 'center',
                    bgcolor: 'rgba(15, 76, 69, 0.1)',
                    color: 'primary.main',
                  }}
                >
                  <FileDownloadOutlinedIcon />
                </Box>
                <Box>
                  <Typography variant="h3">{t('settingsPrivacy.export.title')}</Typography>
                  <Typography color="text.secondary" sx={{ mt: 0.75 }}>
                    {t('settingsPrivacy.export.copy')}
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  startIcon={
                    exportingData ? <CircularProgress color="inherit" size={18} /> : <FileDownloadOutlinedIcon />
                  }
                  onClick={handleDownloadMyData}
                  disabled={exportingData}
                >
                  {exportingData ? t('profile.data.downloading') : t('profile.data.download')}
                </Button>
                {exportSuccess ? (
                  <Alert severity="success" sx={{ width: '100%' }}>
                    {t('profile.data.success')}
                  </Alert>
                ) : null}
                {exportError ? (
                  <Alert severity="error" sx={{ width: '100%' }}>
                    {t('profile.data.error')}
                  </Alert>
                ) : null}
              </Stack>
            </CardContent>
          </Card>

          <Card variant="outlined">
            <CardContent>
              <Stack spacing={2}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <TuneOutlinedIcon color="secondary" />
                  <Typography variant="h4">{t('settings.sections.privacy.cookies')}</Typography>
                </Stack>
                <CookiePreferencesPanel />
              </Stack>
            </CardContent>
          </Card>
        </Box>
      </SettingsSection>

      <SettingsSection
        eyebrow={t('settings.sections.legal.eyebrow')}
        title={t('settings.sections.legal.title')}
        subtitle={t('settings.sections.legal.subtitle')}
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' },
            gap: 2,
          }}
        >
          {legalCards.map(({ icon: Icon, title, copy, to, action }) => (
            <Card key={to} variant="outlined">
              <CardContent>
                <Stack spacing={1.5} alignItems="flex-start">
                  <Icon color="secondary" />
                  <Typography variant="h3">{title}</Typography>
                  <Typography color="text.secondary">{copy}</Typography>
                  <Button component={RouterLink} to={to} variant="outlined">
                    {action}
                  </Button>
                </Stack>
              </CardContent>
            </Card>
          ))}
        </Box>
      </SettingsSection>

      <SettingsSection
        eyebrow={t('settings.sections.account.eyebrow')}
        title={t('settings.sections.account.title')}
        subtitle={t('settings.sections.account.subtitle')}
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
            gap: 2,
          }}
        >
          <Card variant="outlined">
            <CardContent>
              <Stack spacing={1}>
                <Typography variant="h3">{t('settingsPrivacy.contact.title')}</Typography>
                <Typography color="text.secondary">
                  {t('settingsPrivacy.contact.copy')}{' '}
                  <Link href="mailto:contact@thetavern.fr">contact@thetavern.fr</Link>
                </Typography>
              </Stack>
            </CardContent>
          </Card>

          <Card variant="outlined" sx={{ borderColor: 'error.light' }}>
            <CardContent>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={2}
                alignItems={{ xs: 'stretch', sm: 'center' }}
              >
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle2" color="error" sx={{ mb: 0.5 }}>
                    {t('settingsPrivacy.danger.eyebrow')}
                  </Typography>
                  <Typography variant="h3">{t('profile.delete.title')}</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                    {t('settingsPrivacy.danger.copy')}
                  </Typography>
                </Box>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteForeverOutlinedIcon />}
                  onClick={() => {
                    setDeleteError(false);
                    setDeleteDialogOpen(true);
                  }}
                >
                  {t('profile.delete.open')}
                </Button>
              </Stack>
              {deleteError ? (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {t('profile.delete.error')}
                </Alert>
              ) : null}
            </CardContent>
          </Card>
        </Box>
      </SettingsSection>

      <AccountDeletionDialog
        open={deleteDialogOpen}
        deleting={deletingAccount}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDeleteAccount}
      />
    </Stack>
  );
};

export default PrivacyDataSettingsPage;
